import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Box, Typography, Divider, IconButton, Tooltip } from '@mui/material';
import { ChevronLeft, ChevronRight, Refresh, Add, Upload, Download } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import FormattedShift from '../../models/FormattedShift';
import { RootState } from '../../store/store';
import MobileShiftCard from './MobileShiftCard';
import { changeDateRange, refreshDataWithDateRange, reset } from '../../store/actions/shiftActions';
import UploadSchedules from './UploadSchedules';
import FetchShifts from './FetchShifts';

interface Props {
  ongoingShifts: Array<FormattedShift>;
  scheduledShifts: Array<FormattedShift>;
  completedShifts: Array<FormattedShift>;
  startDate: string;
  endDate: string;
  onDateRangeChange: (startDate: string, endDate: string) => void;
  fetchAllRows: (startDate?: string, endDate?: string) => void;
  resetPage: () => void;
}

const AllShiftsMobile: React.FC<Props> = ({
  ongoingShifts,
  scheduledShifts,
  completedShifts,
  startDate,
  endDate,
  onDateRangeChange,
  fetchAllRows,
  resetPage
}) => {
  const navigate = useNavigate();
  const [uploadSchedulesModal, setUploadSchedulesModal] = useState(false);
  const [fetchShiftsModal, setFetchShiftsModal] = useState(false);

  // Action handlers
  const handleRefresh = () => {
    resetPage();
    fetchAllRows();
  };

  const handleScheduleShift = () => {
    navigate('/app/shift/schedule');
  };

  const handleUploadCSV = () => {
    setUploadSchedulesModal(true);
  };

  const handleFetchShifts = () => {
    setFetchShiftsModal(true);
  };

  const closeUploadSchedulesModal = (result: any) => {
    if (result) {
      fetchAllRows();
    }
    setUploadSchedulesModal(false);
  };

  const closeFetchShiftsModal = (result: any) => {
    if (result) {
      fetchAllRows();
    }
    setFetchShiftsModal(false);
  };
  // Combine all shifts and sort by date
  const allShifts = [
    ...(ongoingShifts || []).map((shift) => ({ ...shift, shiftType: 'ongoing' })),
    ...(scheduledShifts || []).map((shift) => ({ ...shift, shiftType: 'scheduled' })),
    ...(completedShifts || []).map((shift) => ({ ...shift, shiftType: 'completed' }))
  ].sort((a, b) => {
    // Sort by scheduled start date, most recent first
    const dateA = new Date(a.scheduledStartUTC || 0).getTime();
    const dateB = new Date(b.scheduledStartUTC || 0).getTime();
    return dateB - dateA;
  });

  const handlePreviousDateRange = () => {
    const currentStart = moment(startDate);
    const currentEnd = moment(endDate);
    const duration = moment.duration(currentEnd.diff(currentStart));

    const newStartDate = currentStart.subtract(duration).startOf('day').toISOString();
    const newEndDate = currentStart.add(duration).endOf('day').toISOString();

    onDateRangeChange(newStartDate, newEndDate);
    fetchAllRows(newStartDate, newEndDate);
  };

  const handleNextDateRange = () => {
    const currentStart = moment(startDate);
    const currentEnd = moment(endDate);
    const duration = moment.duration(currentEnd.diff(currentStart));

    const newStartDate = currentStart.add(duration).startOf('day').toISOString();
    const newEndDate = currentStart.add(duration).endOf('day').toISOString();

    onDateRangeChange(newStartDate, newEndDate);
    fetchAllRows(newStartDate, newEndDate);
  };

  const formatDateRange = () => {
    const start = moment(startDate);
    const end = moment(endDate);

    if (start.isSame(end, 'day')) {
      return start.format('DD MMM YYYY');
    }

    if (start.isSame(end, 'month')) {
      return `${start.format('DD')} - ${end.format('DD MMM YYYY')}`;
    }

    if (start.isSame(end, 'year')) {
      return `${start.format('DD MMM')} - ${end.format('DD MMM YYYY')}`;
    }

    return `${start.format('DD MMM YYYY')} - ${end.format('DD MMM YYYY')}`;
  };

  return (
    <Box sx={{ p: 2, position: 'relative' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2
        }}
      >
        <Typography variant="h6">
          All Shifts ({allShifts.length})
        </Typography>

        {/* Action buttons inline with title */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Refresh shifts">
            <IconButton
              onClick={handleRefresh}
              size="small"
              sx={{
                backgroundColor: 'white',
                border: '1px solid',
                borderColor: 'grey.300',
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'grey.50',
                  borderColor: 'grey.400',
                }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>

          <Tooltip title="Schedule a shift">
            <IconButton
              onClick={handleScheduleShift}
              size="small"
              sx={{
                backgroundColor: 'white',
                border: '1px solid',
                borderColor: 'grey.300',
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'grey.50',
                  borderColor: 'grey.400',
                }
              }}
            >
              <Add />
            </IconButton>
          </Tooltip>

          <Tooltip title="Upload CSV">
            <IconButton
              onClick={handleUploadCSV}
              size="small"
              sx={{
                backgroundColor: 'white',
                border: '1px solid',
                borderColor: 'grey.300',
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'grey.50',
                  borderColor: 'grey.400',
                }
              }}
            >
              <Upload />
            </IconButton>
          </Tooltip>

          <Tooltip title="Fetch Shifts">
            <IconButton
              onClick={handleFetchShifts}
              size="small"
              sx={{
                backgroundColor: 'white',
                border: '1px solid',
                borderColor: 'grey.300',
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'grey.50',
                  borderColor: 'grey.400',
                }
              }}
            >
              <Download />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Divider sx={{ mb: 2 }} />

      {/* Date Range Navigation Header with Actions */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          px: 1,
          backgroundColor: '#f5f5f5',
          borderRadius: 1,
          py: 1
        }}
      >
        <Tooltip title="Previous date range">
          <IconButton
            onClick={handlePreviousDateRange}
            size="small"
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              }
            }}
          >
            <ChevronLeft />
          </IconButton>
        </Tooltip>

        <Box sx={{ textAlign: 'center', flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Date Range
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {formatDateRange()}
          </Typography>
        </Box>

        <Tooltip title="Next date range">
          <IconButton
            onClick={handleNextDateRange}
            size="small"
            sx={{
              backgroundColor: 'primary.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'primary.dark',
              }
            }}
          >
            <ChevronRight />
          </IconButton>
        </Tooltip>
      </Box>

      {allShifts.map((shift) => (
        <MobileShiftCard
          key={`${shift.shiftType}-${shift.id}`}
          shift={shift}
          shiftType={shift.shiftType}
        />
      ))}

      {/* Bottom Navigation - Same as top for convenience */}
      {allShifts.length > 5 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              py: 1
            }}
          >
            <Tooltip title="Previous date range">
              <IconButton
                onClick={handlePreviousDateRange}
                size="small"
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }}
              >
                <ChevronLeft />
              </IconButton>
            </Tooltip>

            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatDateRange()}
            </Typography>

            <Tooltip title="Next date range">
              <IconButton
                onClick={handleNextDateRange}
                size="small"
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                }}
              >
                <ChevronRight />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}

      {/* Modals */}
      <UploadSchedules
        open={uploadSchedulesModal}
        handleModalClose={closeUploadSchedulesModal}
      />
      <FetchShifts
        open={fetchShiftsModal}
        handleModalClose={closeFetchShiftsModal}
      />
    </Box>
  );
};

const mapStateToProps = (state: RootState) => ({
  ongoingShifts: state.shift?.ongoing?.filteredRows || [],
  scheduledShifts: state.shift?.scheduled?.filteredRows || [],
  completedShifts: state.shift?.completed?.filteredRows || [],
  startDate: state.shift?.startDate || '',
  endDate: state.shift?.endDate || ''
});

const mapDispatchToProps = (dispatch: any) => ({
  onDateRangeChange: (startDate: string, endDate: string) => dispatch(changeDateRange(startDate, endDate)),
  fetchAllRows: (startDate?: string, endDate?: string) => dispatch(refreshDataWithDateRange(startDate, endDate)),
  resetPage: () => dispatch(reset())
});

export default connect(mapStateToProps, mapDispatchToProps)(AllShiftsMobile);
